import * as React from 'react';
import {tokens} from '@packages/utilities/styled';
import {ActionPreset} from '@packages/systems/ix3/core';
import {
  IX3_ACTION_DURATION,
  IX3_ACTION_ANIMATION_PROPS,
  IX3_ACTION_DURATION_DRAMATIC,
  IX3_ACTION_DURATION_MEDIUM,
} from './constants';
import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';
import {ActionAnimate64Icon, ActionSet64Icon} from '@packages/systems/spring';

interface BaseIconProps {
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  backgroundPrimary?: string;
  backgroundTertiary?: string;
  borderColor?: string;
  textColor?: string;
}

const StaggeredFadeOutIcon = ({
  width = 64,
  height = 64,
  style,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <g filter="url(#filter0_di_5908_53153)">
      <rect
        x="59"
        y="17"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 59 17)"
        fill={borderColor}
        opacity="0.2"
        data-ix3-icon-part="column-3"
      />
    </g>
    <g filter="url(#filter1_di_5908_53153)">
      <rect
        x="40"
        y="17"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 40 17)"
        fill={borderColor}
        opacity="0.4"
        data-ix3-icon-part="column-2"
      />
    </g>
    <g filter="url(#filter2_di_5908_53153)">
      <rect
        x="21"
        y="17"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 21 17)"
        fill={borderColor}
        data-ix3-icon-part="column-1"
      />
    </g>
    <g opacity="0.5">
      <g clipPath="url(#clip0_5908_53153)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M33.2929 32L29.6465 28.3536L30.3536 27.6465L34.7071 32L30.3536 36.3536L29.6465 35.6465L33.2929 32Z"
          fill={textColor}
        />
      </g>
    </g>
    <g opacity="0.3">
      <g clipPath="url(#clip1_5908_53153)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M52.2929 32L48.6465 28.3536L49.3536 27.6465L53.7071 32L49.3536 36.3536L48.6465 35.6465L52.2929 32Z"
          fill={textColor}
        />
      </g>
    </g>
    <g clipPath="url(#clip2_5908_53153)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.2929 32L10.6465 28.3536L11.3536 27.6465L15.7071 32L11.3536 36.3536L10.6465 35.6465L14.2929 32Z"
        fill={textColor}
      />
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53153"
        x="42"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53153"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53153"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53153"
        />
      </filter>
      <filter
        id="filter1_di_5908_53153"
        x="23"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53153"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53153"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53153"
        />
      </filter>
      <filter
        id="filter2_di_5908_53153"
        x="4"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53153"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53153"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53153"
        />
      </filter>
      <clipPath id="clip0_5908_53153">
        <path
          d="M24 26C24 24.8954 24.8954 24 26 24H38C39.1046 24 40 24.8954 40 26V38C40 39.1046 39.1046 40 38 40H26C24.8954 40 24 39.1046 24 38V26Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip1_5908_53153">
        <path
          d="M43 26C43 24.8954 43.8954 24 45 24H57C58.1046 24 59 24.8954 59 26V38C59 39.1046 58.1046 40 57 40H45C43.8954 40 43 39.1046 43 38V26Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip2_5908_53153">
        <path
          d="M5 26C5 24.8954 5.89543 24 7 24H19C20.1046 24 21 24.8954 21 26V38C21 39.1046 20.1046 40 19 40H7C5.89543 40 5 39.1046 5 38V26Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const StaggeredFadeInDropIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <rect
      x="20.5"
      y="23.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 20.5 23.5)"
      stroke={borderColor}
    />
    <rect
      x="39.5"
      y="23.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 39.5 23.5)"
      stroke={borderColor}
    />
    <rect
      x="58.5"
      y="23.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 58.5 23.5)"
      stroke={borderColor}
    />
    <g filter="url(#filter0_di_5908_53176)">
      <rect
        x="21"
        y="8"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 21 8)"
        fill={backgroundTertiary}
        style={{filter: 'brightness(0.8)'}}
        data-ix3-icon-part="column-1"
      />
    </g>
    <g filter="url(#filter1_di_5908_53176)">
      <rect
        x="40"
        y="14"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 40 14)"
        fill={backgroundTertiary}
        data-ix3-icon-part="column-2"
      />
    </g>
    <g filter="url(#filter2_di_5908_53176)">
      <rect
        x="59"
        y="20"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 59 20)"
        fill={backgroundTertiary}
        style={{filter: 'brightness(1.3)'}}
        data-ix3-icon-part="column-3"
      />
    </g>
    <g clipPath="url(#clip0_5908_53176)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M51 36.2929L54.6464 32.6464L55.3535 33.3535L51 37.7071L46.6464 33.3535L47.3535 32.6464L51 36.2929Z"
        fill={textColor}
      />
    </g>
    <g opacity="0.3">
      <g clipPath="url(#clip1_5908_53176)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13 24.2929L16.6464 20.6464L17.3535 21.3535L13 25.7071L8.64641 21.3535L9.35352 20.6464L13 24.2929Z"
          fill={textColor}
        />
      </g>
    </g>
    <g opacity="0.5">
      <g clipPath="url(#clip2_5908_53176)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M32 30.2929L35.6464 26.6464L36.3535 27.3535L32 31.7071L27.6464 27.3535L28.3535 26.6464L32 30.2929Z"
          fill={textColor}
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53176"
        x="4"
        y="7.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53176"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53176"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53176"
        />
      </filter>
      <filter
        id="filter1_di_5908_53176"
        x="23"
        y="13.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53176"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53176"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53176"
        />
      </filter>
      <filter
        id="filter2_di_5908_53176"
        x="42"
        y="19.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53176"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53176"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53176"
        />
      </filter>
      <clipPath id="clip0_5908_53176">
        <path
          d="M57 27C58.1046 27 59 27.8954 59 29L59 41C59 42.1046 58.1046 43 57 43L45 43C43.8954 43 43 42.1046 43 41L43 29C43 27.8954 43.8954 27 45 27L57 27Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip1_5908_53176">
        <path
          d="M19 15C20.1046 15 21 15.8954 21 17L21 29C21 30.1046 20.1046 31 19 31L7 31C5.89543 31 5 30.1046 5 29L5 17C5 15.8954 5.89543 15 7 15L19 15Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip2_5908_53176">
        <path
          d="M38 21C39.1046 21 40 21.8954 40 23L40 35C40 36.1046 39.1046 37 38 37L26 37C24.8954 37 24 36.1046 24 35L24 23C24 21.8954 24.8954 21 26 21L38 21Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const SlideInIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundPrimary = tokens.colors.backgroundPrimary,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53091"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="4"
      y="17"
      width="51"
      height="30"
    >
      <rect
        x="55"
        y="17"
        width="30"
        height="51"
        rx="2"
        transform="rotate(90 55 17)"
        fill={backgroundPrimary}
      />
    </mask>
    <g mask="url(#mask0_5908_53091)">
      <g filter="url(#filter0_di_5908_53091)">
        <rect
          x="34"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 34 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g filter="url(#filter1_di_5908_53091)">
        <rect
          x="41"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 41 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53091)">
        <rect
          x="48"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 48 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53091)">
        <rect
          x="55"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 55 17)"
          fill={backgroundTertiary}
          style={{filter: 'brightness(1.5)'}}
          data-ix3-icon-part="layer-4"
        />
      </g>
      <g clipPath="url(#clip0_5908_53091)">
        <path
          d="M44.2929 31L41.1464 27.8536L41.8536 27.1465L46.2071 31.5L41.8536 35.8536L41.1464 35.1465L44.2929 32H35V31H44.2929Z"
          fill={textColor}
          data-ix3-icon-part="arrow"
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53091"
        x="3"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53091"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53091"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53091"
        />
      </filter>
      <filter
        id="filter1_di_5908_53091"
        x="10"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53091"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53091"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53091"
        />
      </filter>
      <filter
        id="filter2_di_5908_53091"
        x="17"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53091"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53091"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53091"
        />
      </filter>
      <filter
        id="filter3_di_5908_53091"
        x="24"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53091"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53091"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53091"
        />
      </filter>
      <clipPath id="clip0_5908_53091">
        <path
          d="M32 26C32 24.8954 32.8954 24 34 24H46C47.1046 24 48 24.8954 48 26V38C48 39.1046 47.1046 40 46 40H34C32.8954 40 32 39.1046 32 38V26Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const StaggeredFadeInIcon = ({
  width = 64,
  height = 64,
  style,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <g filter="url(#filter0_di_5908_53143)">
      <rect
        x="21"
        y="17"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 21 17)"
        fill={borderColor}
        opacity="0.2"
        data-ix3-icon-part="column-1"
      />
    </g>
    <g filter="url(#filter1_di_5908_53143)">
      <rect
        x="40"
        y="17"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 40 17)"
        fill={borderColor}
        opacity="0.4"
        data-ix3-icon-part="column-2"
      />
    </g>
    <g filter="url(#filter2_di_5908_53143)">
      <rect
        x="59"
        y="17"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 59 17)"
        fill={borderColor}
        data-ix3-icon-part="column-3"
      />
    </g>
    <g opacity="0.3">
      <g clipPath="url(#clip0_5908_53143)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.2929 32L10.6465 28.3536L11.3536 27.6465L15.7071 32L11.3536 36.3536L10.6465 35.6465L14.2929 32Z"
          fill={textColor}
        />
      </g>
    </g>
    <g opacity="0.5">
      <g clipPath="url(#clip1_5908_53143)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M33.2929 32L29.6465 28.3536L30.3536 27.6465L34.7071 32L30.3536 36.3536L29.6465 35.6465L33.2929 32Z"
          fill={textColor}
        />
      </g>
    </g>
    <g clipPath="url(#clip2_5908_53143)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M52.2929 32L48.6465 28.3536L49.3536 27.6465L53.7071 32L49.3536 36.3536L48.6465 35.6465L52.2929 32Z"
        fill={textColor}
      />
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53143"
        x="4"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53143"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53143"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53143"
        />
      </filter>
      <filter
        id="filter1_di_5908_53143"
        x="23"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53143"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53143"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53143"
        />
      </filter>
      <filter
        id="filter2_di_5908_53143"
        x="42"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53143"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53143"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53143"
        />
      </filter>
      <clipPath id="clip0_5908_53143">
        <path
          d="M5 26C5 24.8954 5.89543 24 7 24H19C20.1046 24 21 24.8954 21 26V38C21 39.1046 20.1046 40 19 40H7C5.89543 40 5 39.1046 5 38V26Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip1_5908_53143">
        <path
          d="M24 26C24 24.8954 24.8954 24 26 24H38C39.1046 24 40 24.8954 40 26V38C40 39.1046 39.1046 40 38 40H26C24.8954 40 24 39.1046 24 38V26Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip2_5908_53143">
        <path
          d="M43 26C43 24.8954 43.8954 24 45 24H57C58.1046 24 59 24.8954 59 26V38C59 39.1046 58.1046 40 57 40H45C43.8954 40 43 39.1046 43 38V26Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

export const SlideInUpIcon = ({
  width = 64,
  height = 64,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
  style,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53102"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="17"
      y="9"
      width="30"
      height="51"
    >
      <rect
        x="47"
        y="60"
        width="30"
        height="51"
        rx="2"
        transform="rotate(-180 47 60)"
        fill={backgroundTertiary}
      />
    </mask>
    <g mask="url(#mask0_5908_53102)">
      <g filter="url(#filter0_di_5908_53102)">
        <rect
          x="47"
          y="60"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 60)"
          fill={borderColor}
          opacity="0.2"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g filter="url(#filter1_di_5908_53102)">
        <rect
          x="47"
          y="53"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 53)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53102)">
        <rect
          x="47"
          y="46"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 46)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53102)">
        <rect
          x="47"
          y="9"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 47 9)"
          fill={backgroundTertiary}
          style={{filter: 'brightness(1.3)'}}
          data-ix3-icon-part="layer-4"
        />
      </g>
      <g clipPath="url(#clip0_5908_53102)">
        <path
          d="M33.0001 19.7072L36.1465 22.8536L36.8536 22.1465L32.5 17.7929L28.1465 22.1465L28.8536 22.8536L32.0001 19.7071L32.0001 29H33.0001L33.0001 19.7072Z"
          fill={textColor}
          data-ix3-icon-part="arrow"
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53102"
        x="16"
        y="29"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53102"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53102"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53102"
        />
      </filter>
      <filter
        id="filter1_di_5908_53102"
        x="16"
        y="22"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53102"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53102"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53102"
        />
      </filter>
      <filter
        id="filter2_di_5908_53102"
        x="16"
        y="15"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53102"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53102"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53102"
        />
      </filter>
      <filter
        id="filter3_di_5908_53102"
        x="16"
        y="8"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53102"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53102"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53102"
        />
      </filter>
      <clipPath id="clip0_5908_53102">
        <path
          d="M24 18C24 16.8954 24.8954 16 26 16H38C39.1046 16 40 16.8954 40 18V30C40 31.1046 39.1046 32 38 32H26C24.8954 32 24 31.1046 24 30V18Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const SlideOutDownIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundPrimary = tokens.colors.backgroundPrimary,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53113"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="17"
      y="9"
      width="30"
      height="51"
    >
      <rect
        x="47"
        y="60"
        width="30"
        height="51"
        rx="2"
        transform="rotate(-180 47 60)"
        fill={backgroundPrimary}
      />
    </mask>
    <g mask="url(#mask0_5908_53113)">
      <g opacity="0.7" filter="url(#filter0_di_5908_53113)">
        <rect
          x="47"
          y="60"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 60)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g opacity="0.5" filter="url(#filter1_di_5908_53113)">
        <rect
          x="47"
          y="53"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 53)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53113)">
        <rect
          x="47"
          y="46"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 46)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53113)">
        <rect
          x="47"
          y="9"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 47 9)"
          fill={backgroundTertiary}
          style={{filter: 'brightness(1.3)'}}
          data-ix3-icon-part="layer-4"
        />
      </g>
      <g clipPath="url(#clip0_5908_53113)">
        <path
          d="M32 28.2929L28.8536 25.1464L28.1465 25.8536L32.5 30.2071L36.8536 25.8536L36.1465 25.1464L33 28.2929L33 19H32L32 28.2929Z"
          fill={textColor}
          data-ix3-icon-part="arrow"
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53113"
        x="16"
        y="29"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53113"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53113"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53113"
        />
      </filter>
      <filter
        id="filter1_di_5908_53113"
        x="16"
        y="22"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53113"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53113"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53113"
        />
      </filter>
      <filter
        id="filter2_di_5908_53113"
        x="16"
        y="15"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53113"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53113"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53113"
        />
      </filter>
      <filter
        id="filter3_di_5908_53113"
        x="16"
        y="8"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53113"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53113"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53113"
        />
      </filter>
      <clipPath id="clip0_5908_53113">
        <path
          d="M24 18C24 16.8954 24.8954 16 26 16H38C39.1046 16 40 16.8954 40 18V30C40 31.1046 39.1046 32 38 32H26C24.8954 32 24 31.1046 24 30V18Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const StaggeredFadeFallOutIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <rect
      x="58.5"
      y="11.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 58.5 11.5)"
      stroke={borderColor}
    />
    <rect
      x="39.5"
      y="11.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 39.5 11.5)"
      stroke={borderColor}
    />
    <rect
      x="20.5"
      y="11.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 20.5 11.5)"
      stroke={borderColor}
    />
    <g filter="url(#filter0_di_5908_53163)">
      <rect
        x="59"
        y="26"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 59 26)"
        fill={backgroundTertiary}
        style={{filter: 'brightness(0.8)'}}
        data-ix3-icon-part="column-3"
      />
    </g>
    <g filter="url(#filter1_di_5908_53163)">
      <rect
        x="40"
        y="20"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 40 20)"
        fill={backgroundTertiary}
        data-ix3-icon-part="column-2"
      />
    </g>
    <g filter="url(#filter2_di_5908_53163)">
      <rect
        x="21"
        y="14"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 21 14)"
        fill={backgroundTertiary}
        style={{filter: 'brightness(1.3)'}}
        data-ix3-icon-part="column-1"
      />
    </g>
    <g clipPath="url(#clip0_5908_53163)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13 30.2929L16.6464 26.6464L17.3535 27.3535L13 31.7071L8.64641 27.3535L9.35352 26.6464L13 30.2929Z"
        fill={textColor}
      />
    </g>
    <g opacity="0.3">
      <g clipPath="url(#clip1_5908_53163)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M51 42.2929L54.6464 38.6464L55.3535 39.3535L51 43.7071L46.6464 39.3535L47.3535 38.6464L51 42.2929Z"
          fill={textColor}
        />
      </g>
    </g>
    <g opacity="0.5">
      <g clipPath="url(#clip2_5908_53163)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M32 36.2929L35.6464 32.6464L36.3535 33.3535L32 37.7071L27.6464 33.3535L28.3535 32.6464L32 36.2929Z"
          fill={textColor}
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53163"
        x="42"
        y="25.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53163"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53163"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53163"
        />
      </filter>
      <filter
        id="filter1_di_5908_53163"
        x="23"
        y="19.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53163"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53163"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53163"
        />
      </filter>
      <filter
        id="filter2_di_5908_53163"
        x="4"
        y="13.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53163"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53163"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53163"
        />
      </filter>
      <clipPath id="clip0_5908_53163">
        <path
          d="M19 21C20.1046 21 21 21.8954 21 23L21 35C21 36.1046 20.1046 37 19 37L7 37C5.89543 37 5 36.1046 5 35L5 23C5 21.8954 5.89543 21 7 21L19 21Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip1_5908_53163">
        <path
          d="M57 33C58.1046 33 59 33.8954 59 35L59 47C59 48.1046 58.1046 49 57 49L45 49C43.8954 49 43 48.1046 43 47L43 35C43 33.8954 43.8954 33 45 33L57 33Z"
          fill={textColor}
        />
      </clipPath>
      <clipPath id="clip2_5908_53163">
        <path
          d="M38 27C39.1046 27 40 27.8954 40 29L40 41C40 42.1046 39.1046 43 38 43L26 43C24.8954 43 24 42.1046 24 41L24 29C24 27.8954 24.8954 27 26 27L38 27Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const FadeOutIcon = ({
  width = 64,
  height = 64,
  style,
  borderColor = tokens.colors.borderDefault,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53225"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="17"
      y="17"
      width="30"
      height="30"
    >
      <rect
        x="17"
        y="47"
        width="30"
        height="30"
        rx="2"
        transform="rotate(-90 17 47)"
        fill="#D9D9D9"
      />
    </mask>
    <g mask="url(#mask0_5908_53225)">
      <g filter="url(#filter0_di_5908_53225)">
        <rect
          x="47"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 47 17)"
          fill={borderColor}
          opacity="0.2"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g filter="url(#filter1_di_5908_53225)">
        <rect
          x="40"
          y="17"
          width="30"
          height="23"
          rx="2"
          transform="rotate(90 40 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53225)">
        <rect
          x="33"
          y="17"
          width="30"
          height="16"
          rx="2"
          transform="rotate(90 33 17)"
          fill={borderColor}
          opacity="0.6"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53225)">
        <rect
          x="26"
          y="17"
          width="30"
          height="9"
          rx="2"
          transform="rotate(90 26 17)"
          fill={borderColor}
          data-ix3-icon-part="layer-4"
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53225"
        x="16"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53225"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53225"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53225"
        />
      </filter>
      <filter
        id="filter1_di_5908_53225"
        x="16"
        y="16.5"
        width="25"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53225"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53225"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53225"
        />
      </filter>
      <filter
        id="filter2_di_5908_53225"
        x="16"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53225"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53225"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53225"
        />
      </filter>
      <filter
        id="filter3_di_5908_53225"
        x="16"
        y="16.5"
        width="11"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53225"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53225"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53225"
        />
      </filter>
    </defs>
  </svg>
);

const SlideOutIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundPrimary = tokens.colors.backgroundPrimary,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53199"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="9"
      y="17"
      width="51"
      height="30"
    >
      <rect
        x="60"
        y="17"
        width="30"
        height="51"
        rx="2"
        transform="rotate(90 60 17)"
        fill={backgroundPrimary}
      />
    </mask>
    <g mask="url(#mask0_5908_53199)">
      <g filter="url(#filter0_di_5908_53199)">
        <rect
          x="60"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 60 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g filter="url(#filter1_di_5908_53199)">
        <rect
          x="53"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 53 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53199)">
        <rect
          x="46"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 46 17)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53199)">
        <rect
          x="39"
          y="17"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 39 17)"
          fill={backgroundTertiary}
          style={{filter: 'brightness(1.5)'}}
          data-ix3-icon-part="layer-4"
        />
      </g>
      <g clipPath="url(#clip0_5908_53199)">
        <path
          d="M28.2929 31L25.1464 27.8536L25.8536 27.1465L30.2071 31.5L25.8536 35.8536L25.1464 35.1465L28.2929 32H19V31H28.2929Z"
          fill={textColor}
          data-ix3-icon-part="arrow"
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53199"
        x="29"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53199"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53199"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53199"
        />
      </filter>
      <filter
        id="filter1_di_5908_53199"
        x="22"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53199"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53199"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53199"
        />
      </filter>
      <filter
        id="filter2_di_5908_53199"
        x="15"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53199"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53199"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53199"
        />
      </filter>
      <filter
        id="filter3_di_5908_53199"
        x="8"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53199"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53199"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53199"
        />
      </filter>
      <clipPath id="clip0_5908_53199">
        <path
          d="M16 26C16 24.8954 16.8954 24 18 24H30C31.1046 24 32 24.8954 32 26V38C32 39.1046 31.1046 40 30 40H18C16.8954 40 16 39.1046 16 38V26Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

const RandomStaggerIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <rect
      x="58.5"
      y="17.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 58.5 17.5)"
      stroke={borderColor}
    />
    <rect
      x="39.5"
      y="17.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 39.5 17.5)"
      stroke={borderColor}
    />
    <rect
      x="20.5"
      y="17.5"
      width="29"
      height="15"
      rx="1.5"
      transform="rotate(90 20.5 17.5)"
      stroke={borderColor}
    />
    <g filter="url(#filter0_di_5908_53189)">
      <rect
        x="59"
        y="14"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 59 14)"
        fill={backgroundTertiary}
        data-ix3-icon-part="column-3"
      />
    </g>
    <g filter="url(#filter1_di_5908_53189)">
      <rect
        x="21"
        y="12"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 21 12)"
        fill={backgroundTertiary}
        style={{filter: 'brightness(0.8)'}}
        data-ix3-icon-part="column-1"
      />
    </g>
    <g filter="url(#filter2_di_5908_53189)">
      <rect
        x="40"
        y="8"
        width="30"
        height="16"
        rx="2"
        transform="rotate(90 40 8)"
        fill={backgroundTertiary}
        style={{filter: 'brightness(1.3)'}}
        data-ix3-icon-part="column-2"
      />
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53189"
        x="42"
        y="13.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53189"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53189"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53189"
        />
      </filter>
      <filter
        id="filter1_di_5908_53189"
        x="4"
        y="11.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53189"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53189"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53189"
        />
      </filter>
      <filter
        id="filter2_di_5908_53189"
        x="23"
        y="7.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53189"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53189"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53189"
        />
      </filter>
    </defs>
  </svg>
);

export const PopInIcon = ({
  width = 64,
  height = 64,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
  style,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <rect
      x="44"
      y="44"
      width="24"
      height="24"
      rx="12"
      transform="rotate(-180 44 44)"
      fill={borderColor}
      opacity="0.6"
      data-ix3-icon-part="halo"
    />
    <g filter="url(#filter0_di_5908_53249)">
      <path
        d="M32 13C42.4934 13 51 21.5066 51 32C51 42.4934 42.4934 51 32 51C21.5066 51 13 42.4934 13 32C13 21.5066 21.5066 13 32 13ZM32 20C25.3726 20 20 25.3726 20 32C20 38.6274 25.3726 44 32 44C38.6274 44 44 38.6274 44 32C44 25.3726 38.6274 20 32 20Z"
        fill={backgroundTertiary}
        style={{filter: 'brightness(1.4)'}}
        data-ix3-icon-part="ring"
      />
    </g>
    <rect
      x="38"
      y="38"
      width="12"
      height="12"
      rx="6"
      transform="rotate(-180 38 38)"
      fill={backgroundTertiary}
      style={{filter: 'brightness(0.7)'}}
      data-ix3-icon-part="core"
    />
    <g opacity="0.4">
      <path
        d="M51.5 12.5L47.5 16.5"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-ix3-icon-part="spark"
      />
      <path
        d="M46.5 11.5L44.6875 14.0625"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-ix3-icon-part="spark"
      />
      <path
        d="M52.5 17.5L49.9375 19.3125"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-ix3-icon-part="spark"
      />
    </g>
    <g opacity="0.4">
      <path
        d="M12 51.8125L16 47.8125"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-ix3-icon-part="spark"
      />
      <path
        d="M17 52.8125L18.8125 50.25"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-ix3-icon-part="spark"
      />
      <path
        d="M11 46.8125L13.5625 45"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-ix3-icon-part="spark"
      />
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53249"
        x="12"
        y="12"
        width="40"
        height="40"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53249"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53249"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53249"
        />
      </filter>
    </defs>
  </svg>
);

export const FadeInIcon = ({
  width = 64,
  height = 64,
  borderColor = tokens.colors.borderDefault,
  style,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53215"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="17"
      y="17"
      width="30"
      height="30"
    >
      <rect
        x="17"
        y="47"
        width="30"
        height="30"
        rx="2"
        transform="rotate(-90 17 47)"
        fill="#D9D9D9"
      />
    </mask>
    <g mask="url(#mask0_5908_53215)">
      <g filter="url(#filter0_di_5908_53215)">
        <rect
          x="17"
          y="47"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-90 17 47)"
          fill={borderColor}
          opacity="0.2"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g filter="url(#filter1_di_5908_53215)">
        <rect
          x="24"
          y="47"
          width="30"
          height="23"
          rx="2"
          transform="rotate(-90 24 47)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53215)">
        <rect
          x="31"
          y="47"
          width="30"
          height="16"
          rx="2"
          transform="rotate(-90 31 47)"
          fill={borderColor}
          opacity="0.6"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53215)">
        <rect
          x="38"
          y="47"
          width="30"
          height="9"
          rx="2"
          transform="rotate(-90 38 47)"
          fill={borderColor}
          data-ix3-icon-part="layer-4"
        />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53215"
        x="16"
        y="16.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53215"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53215"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53215"
        />
      </filter>
      <filter
        id="filter1_di_5908_53215"
        x="23"
        y="16.5"
        width="25"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53215"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53215"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53215"
        />
      </filter>
      <filter
        id="filter2_di_5908_53215"
        x="30"
        y="16.5"
        width="18"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53215"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53215"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53215"
        />
      </filter>
      <filter
        id="filter3_di_5908_53215"
        x="37"
        y="16.5"
        width="11"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53215"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53215"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53215"
        />
      </filter>
    </defs>
  </svg>
);

export const ScaleInIcon = ({
  width = 64,
  height = 64,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
  style,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <path
      opacity="0.4"
      d="M13.5 45C13.6326 45 13.7597 45.0527 13.8535 45.1465C13.9473 45.2403 14 45.3674 14 45.5V47.293C14 47.4998 14.036 48 14.707 48H16.5C16.6326 48 16.7597 48.0527 16.8535 48.1465C16.9473 48.2403 17 48.3674 17 48.5C17 48.6326 16.9473 48.7597 16.8535 48.8535C16.7597 48.9473 16.6326 49 16.5 49H13.5C13.3674 49 13.2403 48.9473 13.1465 48.8535C13.0527 48.7597 13 48.6326 13 48.5V45.5C13 45.3674 13.0527 45.2403 13.1465 45.1465C13.2403 45.0527 13.3674 45 13.5 45ZM50.5 45C50.6326 45 50.7597 45.0527 50.8535 45.1465C50.9473 45.2403 51 45.3674 51 45.5V48.5C51 48.6326 50.9473 48.7597 50.8535 48.8535C50.7597 48.9473 50.6326 49 50.5 49H47.5C47.3674 49 47.2403 48.9473 47.1465 48.8535C47.0527 48.7597 47 48.6326 47 48.5C47 48.3674 47.0527 48.2403 47.1465 48.1465C47.2403 48.0527 47.3674 48 47.5 48H49.293C49.4998 48 50 47.964 50 47.293V45.5C50 45.3674 50.0527 45.2403 50.1465 45.1465C50.2403 45.0527 50.3674 45 50.5 45ZM16.5 11C16.6326 11 16.7597 11.0527 16.8535 11.1465C16.9473 11.2403 17 11.3674 17 11.5C17 11.6326 16.9473 11.7597 16.8535 11.8535C16.7597 11.9473 16.6326 12 16.5 12H14.707C14.5002 12 14 12.036 14 12.707V14.5C14 14.6326 13.9473 14.7597 13.8535 14.8535C13.7597 14.9473 13.6326 15 13.5 15C13.3674 15 13.2403 14.9473 13.1465 14.8535C13.0527 14.7597 13 14.6326 13 14.5V11.5C13 11.3674 13.0527 11.2403 13.1465 11.1465C13.2403 11.0527 13.3674 11 13.5 11H16.5ZM50.5 11C50.6326 11 50.7597 11.0527 50.8535 11.1465C50.9473 11.2403 51 11.3674 51 11.5V14.5C51 14.6326 50.9473 14.7597 50.8535 14.8535C50.7597 14.9473 50.6326 15 50.5 15C50.3674 15 50.2403 14.9473 50.1465 14.8535C50.0527 14.7597 50 14.6326 50 14.5V12.707C50 12.5002 49.964 12 49.293 12H47.5C47.3674 12 47.2403 11.9473 47.1465 11.8535C47.0527 11.7597 47 11.6326 47 11.5C47 11.3674 47.0527 11.2403 47.1465 11.1465C47.2403 11.0527 47.3674 11 47.5 11H50.5Z"
      fill={textColor}
      data-ix3-icon-part="outer"
    />
    <g filter="url(#filter0_i_5908_53235)">
      <rect
        x="43"
        y="41"
        width="22"
        height="22"
        rx="2"
        transform="rotate(-180 43 41)"
        fill={borderColor}
        fillOpacity="0.7"
        data-ix3-icon-part="outer"
      />
    </g>
    <g filter="url(#filter1_di_5908_53235)">
      <path
        d="M45 15C46.1046 15 47 15.8954 47 17V43C47 44.1046 46.1046 45 45 45H19C17.8954 45 17 44.1046 17 43V17C17 15.8954 17.8954 15 19 15H45ZM24 21C23.4477 21 23 21.4477 23 22V38C23 38.5523 23.4477 39 24 39H40C40.5523 39 41 38.5523 41 38V22C41 21.4477 40.5523 21 40 21H24Z"
        fill={backgroundTertiary}
        style={{filter: 'brightness(1.4)'}}
        data-ix3-icon-part="inner"
      />
    </g>
    <rect
      x="36"
      y="34"
      width="8"
      height="8"
      transform="rotate(-180 36 34)"
      fill={backgroundTertiary}
      style={{filter: 'brightness(0.7)'}}
      data-ix3-icon-part="center"
    />
    <defs>
      <filter
        id="filter0_i_5908_53235"
        x="21"
        y="19"
        width="22"
        height="22.5"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect1_innerShadow_5908_53235"
        />
      </filter>
      <filter
        id="filter1_di_5908_53235"
        x="16"
        y="14"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53235"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53235"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53235"
        />
      </filter>
    </defs>
  </svg>
);

const DropInIcon = ({
  width = 64,
  height = 64,
  style,
  backgroundTertiary = tokens.colors.backgroundTertiary,
  borderColor = tokens.colors.borderDefault,
  textColor = tokens.colors.textPrimary,
}: BaseIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <mask
      id="mask0_5908_53124"
      style={{maskType: 'alpha'} as React.CSSProperties}
      maskUnits="userSpaceOnUse"
      x="17"
      y="4"
      width="30"
      height="51"
    >
      <rect
        x="47"
        y="4"
        width="51"
        height="30"
        rx="2"
        transform="rotate(90 47 4)"
        fill={backgroundTertiary}
      />
    </mask>
    <g mask="url(#mask0_5908_53124)">
      <g filter="url(#filter0_di_5908_53124)">
        <rect
          x="47"
          y="34"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 34)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-1"
        />
      </g>
      <g filter="url(#filter1_di_5908_53124)">
        <rect
          x="47"
          y="41"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 41)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-2"
        />
      </g>
      <g filter="url(#filter2_di_5908_53124)">
        <rect
          x="47"
          y="48"
          width="30"
          height="30"
          rx="2"
          transform="rotate(-180 47 48)"
          fill={borderColor}
          opacity="0.3"
          data-ix3-icon-part="layer-3"
        />
      </g>
      <g filter="url(#filter3_di_5908_53124)">
        <rect
          x="47"
          y="25"
          width="30"
          height="30"
          rx="2"
          transform="rotate(90 47 25)"
          fill={backgroundTertiary}
          style={{filter: 'brightness(1.3)'}}
          data-ix3-icon-part="layer-4"
        />
      </g>
    </g>
    <g clipPath="url(#clip0_5908_53124)">
      <path
        d="M32 44.2929L28.8536 41.1464L28.1465 41.8536L32.5 46.2071L36.8536 41.8536L36.1465 41.1464L33 44.2929L33 35H32L32 44.2929Z"
        fill={textColor}
        data-ix3-icon-part="arrow"
      />
    </g>
    <g opacity="0.4">
      <path
        d="M12.2812 60.3317L16.2813 56.3317"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.0435 60.6132L18.3319 58.4877"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 54.5697L14.1255 54.2812"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <g opacity="0.4">
      <path
        d="M52.3315 60.3317L48.3315 56.3317"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M52.6133 54.5696L50.4878 54.2812"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M46.5697 60.6132L46.2813 58.4878"
        stroke={textColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <filter
        id="filter0_di_5908_53124"
        x="16"
        y="3.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53124"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53124"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53124"
        />
      </filter>
      <filter
        id="filter1_di_5908_53124"
        x="16"
        y="10.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53124"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53124"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53124"
        />
      </filter>
      <filter
        id="filter2_di_5908_53124"
        x="16"
        y="17.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53124"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53124"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53124"
        />
      </filter>
      <filter
        id="filter3_di_5908_53124"
        x="16"
        y="24.5"
        width="32"
        height="32"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_5908_53124"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_5908_53124"
          result="shape"
        />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="0.5" />
        <feGaussianBlur stdDeviation="0.25" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="shape"
          result="effect2_innerShadow_5908_53124"
        />
      </filter>
      <clipPath id="clip0_5908_53124">
        <path
          d="M24 34C24 32.8954 24.8954 32 26 32H38C39.1046 32 40 32.8954 40 34V46C40 47.1046 39.1046 48 38 48H26C24.8954 48 24 47.1046 24 46V34Z"
          fill={textColor}
        />
      </clipPath>
    </defs>
  </svg>
);

function buildDefaults(): Record<
  string,
  ActionPreset & {icon: React.ReactNode}
> {
  return {
    'preset-custom-animation': {
      type: 'built-in',
      id: 'preset-custom-animation',
      name: 'Animate',
      deleted: false,
      description: 'What will you create?',
      icon: <ActionAnimate64Icon colorVariant="secondary" />,
      action: {
        name: 'Custom Animation',
        timing: {},
        properties: IX3_ACTION_ANIMATION_PROPS,
      },
    },
    'set-action': {
      type: 'built-in',
      id: 'preset-set-action',
      name: 'Set',
      deleted: false,
      description: 'Immediately render a property',
      icon: <ActionSet64Icon colorVariant="secondary" />,
      action: {
        name: 'Set Action',
        timing: {duration: 0},
        properties: {
          [IX3_WF_EXTENSION_KEYS.CLASS]: {
            class: null,
          },
          [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
            autoAlpha: [],
          },
        },
        tt: 3,
      },
    },
  };
}

export function getBuiltInDefaults(): Record<
  string,
  ActionPreset & {icon: React.ReactNode}
> {
  return buildDefaults();
}

export const BUILT_IN_DEFAULTS = getBuiltInDefaults();

export const BUILT_IN_PRESETS: Record<
  string,
  ActionPreset & {icon: React.ReactNode}
> = {
  // Fade In
  'preset-fade-in': {
    type: 'built-in',
    id: 'preset-fade-in',
    deleted: false,
    name: 'Fade in',
    description: 'Smoothly fade element into view',
    icon: <FadeInIcon />,
    action: {
      name: 'Fade In Action',
      timing: {duration: IX3_ACTION_DURATION, ease: 29}, // 'sine.out = 29'
      tt: 0,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: [null, '100%'],
        },
      },
    },
  },
  // Fade Out
  'preset-fade-out': {
    type: 'built-in',
    id: 'preset-fade-out',
    deleted: false,
    name: 'Fade out',
    description: 'Smoothly fade element out of view',
    icon: <FadeOutIcon />,
    action: {
      name: 'Fade Out Action',
      timing: {duration: IX3_ACTION_DURATION, ease: 30}, // 'sine.inOut = 30'
      tt: 0,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: [null, '0%'],
        },
      },
    },
  },
  // Slide In Left
  'preset-slide-in-left': {
    type: 'built-in',
    id: 'preset-slide-in-left',
    deleted: false,
    name: 'Slide in left',
    description: 'Slide in from left',
    icon: <SlideInIcon />,
    action: {
      name: 'Slide In Left',
      timing: {duration: IX3_ACTION_DURATION, ease: 3}, // 'power1.inOut = 3'
      tt: 2,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: ['0%', '100%'],
          xPercent: [-100, 0],
        },
      },
    },
  },
  // Slide Out Right
  'preset-slide-out-right': {
    type: 'built-in',
    id: 'preset-slide-out-right',
    deleted: false,
    name: 'Slide out\n right',
    description: 'Slide out to right',
    icon: <SlideOutIcon />,
    action: {
      name: 'Slide Out Right',
      timing: {duration: IX3_ACTION_DURATION, ease: 30}, // 'sine.inOut = 30'
      tt: 0,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: [null, '0%'],
          xPercent: [null, 100],
        },
      },
    },
  },
  // Slide In Up
  'preset-slide-in-up': {
    type: 'built-in',
    id: 'preset-slide-in-up',
    deleted: false,
    name: 'Slide in up',
    description: 'Slide in from below',
    icon: <SlideInUpIcon />,
    action: {
      name: 'Slide In Up',
      timing: {duration: IX3_ACTION_DURATION_MEDIUM, ease: 9}, // 'power3.inOut = 9'
      tt: 1,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: ['0%', null],
          yPercent: [100, null],
        },
      },
    },
  },
  // Slide Out Down
  'preset-slide-out-down': {
    type: 'built-in',
    id: 'preset-slide-out-down',
    deleted: false,
    name: 'Slide out down',
    description: 'Slide out to below',
    icon: <SlideOutDownIcon />,
    action: {
      name: 'Slide Out Down',
      timing: {duration: IX3_ACTION_DURATION_DRAMATIC, ease: 11}, // 'power4.out' = 11
      tt: 0,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: [null, '0%'],
          yPercent: [null, 100],
        },
      },
    },
  },
  // Scale In
  'preset-scale-in': {
    type: 'built-in',
    id: 'preset-scale-in',
    deleted: false,
    name: 'Scale in',
    description: 'Scale in from center',
    icon: <ScaleInIcon />,
    action: {
      name: 'Scale In',
      timing: {duration: IX3_ACTION_DURATION_DRAMATIC, ease: 26}, // 'expo.out' = 26
      tt: 2,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          scale: [0, 1],
        },
      },
    },
  },
  // Pop In
  'preset-pop-in': {
    type: 'built-in',
    id: 'preset-pop-in',
    deleted: false,
    name: 'Pop in',
    description: 'Pop in with back ease',
    icon: <PopInIcon />,
    action: {
      name: 'Pop In',
      timing: {duration: IX3_ACTION_DURATION_MEDIUM, ease: 14}, // 'back.out' = 14
      tt: 1,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          scale: [0, null],
        },
      },
    },
  },
  // Drop In
  'preset-drop-in': {
    type: 'built-in',
    id: 'preset-drop-in',
    deleted: false,
    name: 'Drop in',
    description: 'Drop in from above with bounce',
    icon: <DropInIcon />,
    action: {
      name: 'Drop In',
      timing: {duration: IX3_ACTION_DURATION_DRAMATIC, ease: 17}, // 'bounce.out' = 17
      tt: 1,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          y: [-300, null],
          opacity: ['0%', null],
        },
      },
    },
  },
  // Staggered Fade In
  'preset-staggered-fade-in': {
    type: 'built-in',
    id: 'preset-staggered-fade-in',
    deleted: false,
    name: 'Stagger\n fade in',
    description: 'Fade in elements with stagger',
    icon: <StaggeredFadeInIcon />,
    action: {
      name: 'Staggered Fade In',
      timing: {
        duration: IX3_ACTION_DURATION_DRAMATIC,
        ease: 5,
        stagger: {each: 0.05},
      }, // 'power2.out' = 5
      tt: 1,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: ['0%', null],
        },
      },
    },
  },
  // Staggered Fade Out
  'preset-staggered-fade-out': {
    type: 'built-in',
    id: 'preset-staggered-fade-out',
    deleted: false,
    name: 'Stagger\n fade out',
    description: 'Fade out elements with stagger',
    icon: <StaggeredFadeOutIcon />,
    action: {
      name: 'Staggered Fade Out',
      timing: {
        duration: IX3_ACTION_DURATION,
        ease: 2,
        stagger: {each: 0.05, from: 'end'},
      }, // 'power1.out' = 2
      tt: 0,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: [null, '0%'],
        },
      },
    },
  },
  // Staggered Fade Drop In
  'preset-staggered-fade-drop-in': {
    type: 'built-in',
    id: 'preset-staggered-fade-drop-in',
    deleted: false,
    name: 'Stagger\n drop in',
    description: 'Fade and drop in elements with stagger',
    icon: <StaggeredFadeInDropIcon />,
    action: {
      name: 'Staggered Fade Drop In',
      timing: {
        duration: IX3_ACTION_DURATION_DRAMATIC,
        ease: 11,
        stagger: {each: 0.05},
      },
      tt: 1,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: ['0%', null],
          yPercent: [-100, null],
        },
      },
    },
  },
  // Staggered Fade Fall Out
  'preset-staggered-fade-fall-out': {
    // +++
    type: 'built-in',
    id: 'preset-staggered-fade-fall-out',
    deleted: false,
    name: 'Stagger\n fall out',
    description: 'Fade and fall out elements with stagger',
    icon: <StaggeredFadeFallOutIcon />,
    action: {
      name: 'Staggered Fade Fall Out',
      timing: {duration: IX3_ACTION_DURATION, ease: 3, stagger: {each: 0.05}}, // 'power1.inOut' = 3
      tt: 0,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: [null, '0%'],
          yPercent: [null, 100],
        },
      },
    },
  },
  // Random Stagger
  'preset-random-stagger': {
    type: 'built-in',
    id: 'preset-random-stagger',
    deleted: false,
    name: 'Random stagger',
    description: 'Random staggered fade and rotation',
    icon: <RandomStaggerIcon />,
    action: {
      name: 'Random Stagger',
      timing: {
        duration: IX3_ACTION_DURATION,
        ease: 7,
        stagger: {each: 0.05, from: 'random'},
      },
      tt: 2,
      properties: {
        [IX3_WF_EXTENSION_KEYS.TRANSFORM]: {
          opacity: ['0%', null],
        },
      },
    },
  },
};
